import { FC } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  Divider,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
} from '@material-ui/core';
import type { SxProps } from '@material-ui/system';
import { usePopover } from '../hooks/use-popover';
import { Selector as SelectorIcon } from '../icons/selector';
import { SUPER_ADMIN_TENANT, Tenant } from '../types/tenant';

interface TenantPopoverProps {
  currentTenant: Tenant;
  onTenantChange: (tenantId: string) => void;
  tenants: Tenant[];
  sx?: SxProps;
}

export const TenantPopover: FC<TenantPopoverProps> = (props) => {
  const { currentTenant, tenants, onTenantChange, sx, ...other } = props;
  const [anchorRef, open, handleOpen, handleClose] = usePopover();

  const handleTenantChange = (tenantId: string): void => {
    handleClose();
    onTenantChange?.(tenantId);
  };

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          borderRadius: 1,
          display: 'flex',
          p: 1,
          width: 180,
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
          },
          ...sx,
        }}
        {...other}
      >
        <Typography
          color="textSecondary"
          sx={{
            whiteSpace: 'nowrap',
            color: 'primary.contrastText',
            mr: 3,
          }}
          variant="subtitle2"
        >
          {currentTenant.name}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <SelectorIcon fontSize="small" />
      </ButtonBase>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: 200 },
        }}
      >
        <List>
          {tenants.map((tenant) => (
            <div key={tenant.id}>
              <ListItem
                button
                selected={tenant.id === currentTenant.id}
                onClick={() => handleTenantChange(tenant.id)}
              >
                <ListItemText primary={tenant.name} />
              </ListItem>
              {tenant.id === SUPER_ADMIN_TENANT.id && tenants.length > 1 && (
                <Divider key="divider" />
              )}
            </div>
          ))}
        </List>
      </Popover>
    </>
  );
};

TenantPopover.propTypes = {
  // @ts-ignore
  currentTenant: PropTypes.object.isRequired,
  onTenantChange: PropTypes.func,
  tenants: PropTypes.array.isRequired,
  sx: PropTypes.object,
};
